import { nextTick, shallowRef, watch, type Ref } from 'vue';
import type { GlobalThemeOverrides } from 'naive-ui';
import { useTheme } from '@/composables/useTheme';

/**
 * Naive UI mavzusi — barcha qiymatlar DS tokenlaridan (`styles/ds-tokens.css`
 * → `styles/tokens.css`) run-time'da o'qiladi. Naive ranglarni JS'da hisoblaydi
 * va CSS `var()` qabul qilmaydi, shuning uchun token qiymatlari
 * `getComputedStyle` bilan olinadi — hex'lar shu faylda TAKRORLANMAYDI.
 *
 * Yorug'/qorong'i: DS `:root[data-theme="dark"]` da qiymatlarni almashtiradi,
 * shuning uchun rejim o'zgarganda tokenlar qayta o'qilsa yetarli.
 *
 * Tipografika: eng kichik 16px (DS text-md) — Naive'ning default 14px emas.
 */
const TOKENS = [
  '--ui-primary',
  '--ui-primary-hover',
  '--ui-primary-pressed',
  '--ui-text',
  '--ui-text-2',
  '--ui-text-3',
  '--ui-placeholder',
  '--ui-border',
  '--ui-border-strong',
  '--ui-surface',
  '--ui-surface-2',
  '--ui-body',
  '--ui-success',
  '--ui-warning',
  '--ui-error',
  '--ui-info',
  '--ui-shadow',
  '--font',
  '--font-size-text-md',
  '--font-size-text-lg',
  '--font-size-text-xl',
  '--r-xs',
  '--r-sm',
  '--r-md',
  '--r-lg',
  '--r-xl',
  '--r-pill',
  '--ctl-sm',
  '--ctl',
  '--ctl-md',
  '--ctl-lg',
  '--s-2',
  '--spacing-2xl',
  '--spacing-3xl',
  '--leading',
  '--font-weight-semibold',
] as const;

type Token = (typeof TOKENS)[number];
type Tokens = Partial<Record<Token, string>>;

/** DS tokenlarining hozirgi (rejimga mos) qiymatlari. Brauzersiz muhitda bo'sh. */
function read(): Tokens {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') return {};
  const cs = getComputedStyle(document.documentElement);
  const out: Tokens = {};
  for (const name of TOKENS) {
    const value = cs.getPropertyValue(name).trim();
    if (value) out[name] = value;
  }
  return out;
}

/** `undefined` qiymatlarni olib tashlaydi — Naive o'z default'ini ishlatsin. */
function defined<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

export function buildThemeOverrides(): GlobalThemeOverrides {
  const t = read();
  return {
    common: defined({
      fontFamily: t['--font'],
      fontSize: t['--font-size-text-md'],
      fontSizeSmall: t['--font-size-text-md'],
      fontSizeMedium: t['--font-size-text-md'],
      fontSizeLarge: t['--font-size-text-lg'],
      fontSizeHuge: t['--font-size-text-xl'],
      lineHeight: t['--leading'],
      borderRadius: t['--r-sm'],
      borderRadiusSmall: t['--r-xs'],
      primaryColor: t['--ui-primary'],
      primaryColorHover: t['--ui-primary-hover'],
      primaryColorPressed: t['--ui-primary-pressed'],
      primaryColorSuppl: t['--ui-primary-hover'],
      successColor: t['--ui-success'],
      warningColor: t['--ui-warning'],
      errorColor: t['--ui-error'],
      infoColor: t['--ui-info'],
      textColorBase: t['--ui-text'],
      textColor1: t['--ui-text'],
      textColor2: t['--ui-text-2'],
      textColor3: t['--ui-text-3'],
      placeholderColor: t['--ui-placeholder'],
      borderColor: t['--ui-border'],
      dividerColor: t['--ui-border'],
      cardColor: t['--ui-surface'],
      modalColor: t['--ui-surface'],
      popoverColor: t['--ui-surface'],
      tableColor: t['--ui-surface'],
      inputColor: t['--ui-surface-2'],
      inputColorDisabled: t['--ui-surface-2'],
      bodyColor: t['--ui-body'],
      hoverColor: t['--ui-surface-2'],
      tableHeaderColor: t['--ui-surface-2'],
      boxShadow2: t['--ui-shadow'],
      boxShadow3: t['--ui-shadow'],
    }),
    Button: defined({
      borderRadiusMedium: t['--r-sm'],
      borderRadiusLarge: t['--r-md'],
      fontWeight: t['--font-weight-semibold'],
      heightMedium: t['--ctl'],
      heightLarge: t['--ctl-lg'],
      heightSmall: t['--ctl-sm'],
      paddingMedium: t['--s-2'] ? `0 ${t['--s-2']}` : undefined,
      fontSizeSmall: t['--font-size-text-md'],
    }),
    Input: defined({
      heightMedium: t['--ctl-md'],
      heightLarge: t['--ctl-lg'],
      heightSmall: t['--ctl'],
      borderRadius: t['--r-sm'],
    }),
    Select: {
      peers: {
        InternalSelection: defined({ heightMedium: t['--ctl-md'], borderRadius: t['--r-sm'] }),
      },
    },
    Card: defined({ borderRadius: t['--r-lg'], borderColor: t['--ui-border'] }),
    DataTable: defined({
      borderRadius: t['--r-lg'],
      thFontWeight: t['--font-weight-semibold'],
      thColor: t['--ui-surface-2'],
      borderColor: t['--ui-border'],
      thTextColor: t['--ui-text-3'],
    }),
    Tag: defined({ borderRadius: t['--r-pill'] }),
    Dialog: defined({ borderRadius: t['--r-lg'] }),
    Modal: { peers: { Card: defined({ borderRadius: t['--r-xl'] }) } },
    Form: defined({
      labelFontSizeTopMedium: t['--font-size-text-md'],
      labelFontWeight: t['--font-weight-semibold'],
      feedbackFontSizeMedium: t['--font-size-text-md'],
    }),
    Pagination: defined({ itemSizeMedium: t['--ctl-sm'], itemBorderRadius: t['--r-sm'] }),
    Tabs: defined({
      tabFontSizeMedium: t['--font-size-text-md'],
      tabFontWeightActive: t['--font-weight-semibold'],
    }),
    Switch: defined({
      railHeightMedium: t['--spacing-3xl'],
      railWidthMedium: t['--ctl'],
      buttonHeightMedium: t['--spacing-2xl'],
      buttonWidthMedium: t['--spacing-2xl'],
    }),
  };
}

/**
 * Mavzu o'zgarganda tokenlarni qayta o'qiydi. `data-theme` atributi
 * `useTheme` ning pre-flush watcher'ida qo'yiladi, shuning uchun qiymatlar
 * `nextTick` dan keyin olinadi.
 */
export function useNaiveThemeOverrides(): Ref<GlobalThemeOverrides> {
  const { isDark } = useTheme();
  const overrides = shallowRef<GlobalThemeOverrides>(buildThemeOverrides());
  watch(isDark, () => {
    void nextTick(() => {
      overrides.value = buildThemeOverrides();
    });
  });
  return overrides;
}
